import type {
  ShippingProtection,
  ShippingServiceCodeName,
} from '../api';

/**
 * Protection opt-out logic — shared by the Delivery New form (auto-select +
 * opt-out gating) and the warning / disclaimer components. Kept framework-free
 * (pure functions + copy constants) so it's trivially unit-testable.
 *
 * A shipping option's `code_name` declares which protections it bundles:
 *   • 'delivery'                → none
 *   • 'insurance'               → insurance only
 *   • 'signature'               → signature only
 *   • 'insurance_and_signature' → both
 */

const INSURANCE_CODES: ReadonlyArray<ShippingServiceCodeName> = [
  'insurance',
  'insurance_and_signature',
];

const SIGNATURE_CODES: ReadonlyArray<ShippingServiceCodeName> = [
  'signature',
  'insurance_and_signature',
];

/** True when the chosen option provides insurance coverage. */
export function coversInsurance(codeName: ShippingServiceCodeName | null | undefined): boolean {
  return codeName != null && INSURANCE_CODES.includes(codeName);
}

/** True when the chosen option provides signature confirmation. */
export function coversSignature(codeName: ShippingServiceCodeName | null | undefined): boolean {
  return codeName != null && SIGNATURE_CODES.includes(codeName);
}

/**
 * True when the player picked an option that drops a protection the backend
 * marked as required for this shipment — i.e. they're opting out. Returns
 * false when there's no protection block, nothing is required, or no option
 * is chosen yet (nothing to opt out of).
 */
export function isProtectionOptOut(
  protection: ShippingProtection | null | undefined,
  chosenCodeName: ShippingServiceCodeName | null | undefined,
): boolean {
  if (!protection) return false;
  if (!chosenCodeName) return false;
  const droppedInsurance = protection.requiredInsurance && !coversInsurance(chosenCodeName);
  const droppedSignature = protection.requiredSignature && !coversSignature(chosenCodeName);
  return droppedInsurance || droppedSignature;
}

/** Warning shown when the player opts out of a required protection. */
export const PROTECTION_OPT_OUT_WARNING =
  "⚠️ You're removing a protection that applies to your order. Repackz is " +
  'not responsible for loss, theft, or damage on uninsured or unsigned shipments.';

/** Informational disclaimer shown when a single card exceeds the max
 *  insurable value. Coverage is capped — no acknowledgement required. */
// TODO(currency): hardcoded currency found, should use sessionStorage currency.
// The $5,000 insurance cap is a business parameter that should come from the
// backend together with operator_currency, then be formatted via formatPrice.
export const HIGH_VALUE_DISCLAIMER =
  'This card exceeds the maximum insurable value of $5,000. Coverage will be ' +
  'capped at $5,000. Repackz is not liable for loss or damage beyond the ' +
  'covered amount. By proceeding, you accept this limitation.';
